import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { ArrowLeft, Share2, Copy, Check, Gift, Users, UserPlus, Award } from "lucide-react";

export default function ReferralPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => { API.get("/referral/code").then(r => setReferralCode(r.data.referral_code || "")).catch(() => {}); }, []);

  const shareText = `Join MyErba Rewards by TransAsia! Use my referral code: ${referralCode} to sign up and earn 100 bonus points. Download now!`;
  const copyCode = () => { navigator.clipboard.writeText(referralCode); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  return (
    <div className="space-y-5" data-testid="referral-page">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-xl font-bold text-gray-900">Refer & Earn</h1>
      </div>

      {/* User Card */}
      <div className="bg-gradient-to-r from-[#008A99] to-[#004A52] rounded-2xl p-5 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center"><Award className="w-6 h-6" /></div>
            <div><p className="text-xs opacity-60">Name</p><p className="font-bold">{user?.full_name || "Member"}</p></div>
          </div>
          <div className="text-right"><p className="text-xs opacity-60">Total Earned Points</p><p className="text-2xl font-black">{(user?.total_points_earned || user?.balance_points || 0).toLocaleString()}</p></div>
        </div>
      </div>

      {/* Invite Section */}
      <div className="bg-white rounded-2xl border p-5 text-center">
        <p className="text-sm font-bold text-gray-900 mb-1">Start Inviting & Earn 100 points!</p>
        <div className="flex items-center justify-center gap-2 bg-gray-50 rounded-xl px-4 py-3 mt-3">
          <span className="text-lg font-black text-[#008A99] tracking-wider" data-testid="referral-code">{referralCode || "Loading..."}</span>
          <button onClick={copyCode} className="flex items-center gap-1 px-3 py-1.5 bg-white border rounded-lg text-xs font-semibold text-gray-600 hover:bg-gray-50" data-testid="copy-code-btn">
            {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />} Copy
          </button>
        </div>
        <button onClick={() => { if (navigator.share) navigator.share({ text: shareText }); else window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`); }}
          className="w-full mt-3 py-3 border-2 border-[#008A99] text-[#008A99] rounded-xl font-semibold text-sm flex items-center justify-center gap-2" data-testid="share-btn">
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-2xl border p-5">
        <p className="text-sm font-bold text-gray-900 mb-4">How to Get Points?</p>
        <div className="flex items-start justify-between">
          <div className="flex flex-col items-center text-center flex-1">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2"><Share2 className="w-5 h-5 text-gray-500" /></div>
            <p className="text-xs font-bold text-gray-700">Step 1</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Share your link with friends</p>
          </div>
          <div className="text-gray-300 mt-5">▸▸▸</div>
          <div className="flex flex-col items-center text-center flex-1">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2"><UserPlus className="w-5 h-5 text-gray-500" /></div>
            <p className="text-xs font-bold text-gray-700">Step 2</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Invite registers, you get Points</p>
          </div>
          <div className="text-gray-300 mt-5">▸▸▸</div>
          <div className="flex flex-col items-center text-center flex-1">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2"><Gift className="w-5 h-5 text-gray-500" /></div>
            <p className="text-xs font-bold text-gray-700">Step 3</p>
            <p className="text-[10px] text-gray-400 mt-0.5">Get 100 Points after registration</p>
          </div>
        </div>
      </div>

      {/* Rewards & History button */}
      <button onClick={() => navigate("/app/points")} className="w-full py-3 bg-[#008A99] text-white rounded-xl font-semibold text-sm" data-testid="rewards-history-btn">
        Rewards & History
      </button>
    </div>
  );
}
